import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Header from "../../../src/component/Header";
import IconDelete from "../../../src/component/SvgIcons/IconDelete";
import IconPen from "../../../src/component/SvgIcons/IconPen";
import IconUpdownFilter from "../../../src/component/SvgIcons/IconUpdownFilter";
import IconSearch from "../../../src/component/SvgIcons/IconSearch";
import api from "../../../src/Services/api";
import Pagination from "../../component/Pagination";
import { useSwal } from "../../component/sweetalert";
import "../Events/style.css";

export default function ConfirmationList() {
  const alert = useSwal();
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");  
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "confirmationId", direction: "desc" });
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Form state
const [formData, setFormData] = useState({ name: "" });
const [editingId, setEditingId] = useState(null); // null = adding, id = editing

// Handle input change
const handleInputChange = (e) => {
  setFormData({ ...formData, [e.target.name]: e.target.value });
};

// Handle submit
const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.name.trim()) {
    alert("error", "Name is required");
    return;
  }

  setLoading(true);
  try {
    let response;

    if (editingId) {
      // ✅ Update existing
      response = await api.post(`/update-confirmation/${editingId}`, formData);

      setUsers((prev) =>
        prev.map((user) =>
          user.confirmationId === editingId ? { ...user, ...formData } : user
        )
      );

      alert("success", response.data.message || "Updated successfully");
    } else {
      // ✅ Add new
      response = await api.post("/confirmation", formData);

      const newUser = {
        confirmationId: response.data.data?.confirmationId || Date.now(),
        name: formData.name,
        status: response.data.data?.status ?? true,
      };

      setUsers((prev) => [newUser, ...prev]);
      alert("success", response.data.message || "Added successfully");
    }

    // ✅ Reset form after success
    setFormData({ name: "" });
    setEditingId(null);
  } catch (err) {
    console.error(err);

    // ✅ Handle duplicate validation
    if (err.response?.status === 400) {
      alert("error", err.response.data.message || "User already exists!");
    } else {
      alert("error", "Something went wrong");
    }
  } finally {
    setLoading(false);
  }
};

// Handle edit button click
const handleEdit = (user) => {
  setFormData({ name: user.name });
  setEditingId(user.confirmationId);
};

  // Adjust items per page on resize
  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(window.innerWidth >= 1920 ? 20 : 10);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.post("/confirmation-list");
        console.log("API Response:", response.data);
        
        // Sort by newest first by default
        setUsers(response.data.slice().sort((a, b) => b.confirmationId - a.confirmationId));
      } catch (error) {
        console.error("Error fetching confirmations:", error);
      }
    };
    fetchUsers();
  }, []);

  // Handle search
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // reset to first page on search
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Handle delete
  const handleDelete = async (confirmationId) => {
    const isConfirmed = await alert(
      "warning",
      "Do you really want to delete this user?",
      "Are you sure?",
      { showCancelButton: true, confirmButtonText: "Yes, delete it!", cancelButtonText: "Cancel" }
    );
    if (!isConfirmed) return;

    try {
      const response = await api.post(`/delete-confirmation/${confirmationId}`);
      alert("success", response.data.message);
      setUsers((prev) => prev.filter((user) => user.confirmationId !== confirmationId));
    } catch (err) {
      console.error("Error deleting confirmation:", err);
      alert("error", err.response?.data?.message || "Failed to delete Event User");
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (id, currentStatus) => {
    const isConfirmed = await alert(
      "warning",
      `Do you really want to ${currentStatus ? "deactivate" : "activate"} this user?`,
      "Are you sure?",
      { showCancelButton: true, confirmButtonText: "Yes", cancelButtonText: "Cancel" }
    );
    if (!isConfirmed) return;

    try {
      await api.post(`/toggle-status/${id}`);
      setUsers((prev) =>
        prev.map((user) =>
          user.confirmationId === id ? { ...user, status: !currentStatus } : user
        )
      );
      alert("success", `User ${currentStatus ? "deactivated" : "activated"} successfully`);
    } catch (error) {
      console.error("Error toggling status:", error);
      alert("error", "Failed to update status", "Oops...");
    }
  };  
 // Filter and sort data before pagination
const filteredAndSortedUsers = useMemo(() => {
  let filtered = users.filter((user) => {
    const userName = user.name || ""; // fallback to empty string
    const nameMatch = userName.toLowerCase().includes(searchQuery.toLowerCase());

    // Convert boolean status to string for search
    const statusText = user.status ? "active" : "inactive";
    const statusMatch = statusText.includes(searchQuery.toLowerCase());

    return nameMatch || statusMatch; // Match name OR status
  });

  if (sortConfig.key) {
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // If sorting by status, convert boolean to string
      if (sortConfig.key === "status") {
        aValue = aValue ? "active" : "inactive";
        bValue = bValue ? "active" : "inactive";
      }

      // fallback to empty string for undefined
      aValue = aValue ?? "";
      bValue = bValue ?? "";

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }
  return filtered;
}, [users, searchQuery, sortConfig]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredAndSortedUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="eventpage">
      <Header pageTitle="Event Users List" />
      <div className="pageLeftspace">
        <div className="d-flex justify-content-between align-items-center topsearchbar">
          <div className="d-flex align-items-center gap-2">          
            <Link to="#" onClick={() => handleSort("name")}>
              <IconUpdownFilter />
            </Link>
          </div>
          <div className="input-container">
            <input
              type="text"
              placeholder="Search Here.."
              value={searchQuery}
              onChange={handleSearchChange}
            />            
            <div className="search-icon">
              <IconSearch />
            </div>
          </div>
        </div>

        <div className="tableheader d-flex flex-column gap-4">
          <div className="event-detailstable">
            <table className="table">
              <thead>
                <tr>
                  <th width="10">Sr. No.</th>
                  <th>Name</th>
                  <th width="100" className="text-center">Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
              <tr>
                <td className="text-center align-middle">{editingId ? "Edit" : "Add"}</td>
                <td>
                  <input
                    type="text"
                    name="name"
                    className="w-100 border p-2 rounded"
                    placeholder="Enter Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </td>
                <td colSpan={2}>
                  <button
                    className="btn btn-purpal w-100 justify-content-center"
                    type="submit"
                    disabled={loading}
                    onClick={handleSubmit}
                  >
                    {loading ? "Submitting..." : editingId ? "Update" : "Submit"}
                  </button>
                </td>
              </tr>

                {currentUsers.length > 0 ? (
                  currentUsers.map((confirmation, index) => (
                    <tr key={confirmation.confirmationId}>
                      <td align="center">{indexOfFirstItem + index + 1}</td>
                      <td>{confirmation.name}</td>
                      <td>
                        <button
                          className={`status-badge border-0 px-3 ${
                            confirmation.status ? "status-active" : "status-deactive"
                          }`}
                          onClick={() =>
                            handleStatusToggle(confirmation.confirmationId, confirmation.status)
                          }
                        >
                          {confirmation.status ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td>
                        <div className="action">                     
                          <div className="editbtn">
                          <button
                            className="border-0 box-shadow-none"
                            style={{ background: "none" }}
                            onClick={() => handleEdit(confirmation)}
                            data-tooltip-id={`edit-${confirmation.confirmationId}`}
                            data-tooltip-content="Edit"
                          >
                            <IconPen />
                          </button>
                          </div>

                          <Tooltip id={`edit-${confirmation.confirmationId}`} place="top" />
                          <Link
                            onClick={(e) => {
                              e.preventDefault();
                              handleDelete(confirmation.confirmationId);
                            }}
                            data-tooltip-id={`delete-${confirmation.confirmationId}`}
                            data-tooltip-content="Delete"
                          >
                            <IconDelete />
                          </Link>
                          <Tooltip id={`delete-${confirmation.confirmationId}`} place="top" />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center", padding: "10px" }}>
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
}
